import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { ArrowLeft, Loader2, Send, ShieldAlert, Info, Ban } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PO-2026-06 — Discovery conversation (client ↔ talent, pre-booking allowed).
 * Every send passes anti-circumvention moderation:
 *   • block -> red banner, message NOT sent, must edit.
 *   • warn  -> amber banner with Revise / Send anyway.
 *   • allow -> sent.
 */
const DiscoveryMessage = () => {
  const { talentId } = useParams();
  const navigate = useNavigate();
  const [threadId, setThreadId] = useState(null);
  const [other, setOther] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [block, setBlock] = useState(null); // {reason}
  const [warn, setWarn] = useState(null);    // {reason}
  const bottomRef = useRef(null);

  const loadThread = useCallback(async (tid) => {
    const r = await api.get(`/discovery/messages/thread/${tid}`);
    setOther(r.data.other);
    setOrigin(r.data.origin_label);
    setMessages(r.data.messages || []);
  }, []);

  const boot = useCallback(async () => {
    setLoading(true);
    try {
      const open = await api.post('/discovery/messages/open', { talent_id: talentId });
      setThreadId(open.data.thread_id);
      setOther(open.data.talent);
      setOrigin(open.data.origin_label);
      await loadThread(open.data.thread_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Could not open conversation');
    } finally {
      setLoading(false);
    }
  }, [talentId, loadThread]);

  useEffect(() => { boot(); }, [boot]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const doSend = async (acknowledge) => {
    const content = draft.trim();
    if (!content || sending || !threadId) return;
    setSending(true); setBlock(null); if (!acknowledge) setWarn(null);
    try {
      const r = await api.post(`/discovery/messages/thread/${threadId}/send`, {
        content, acknowledge_warning: !!acknowledge,
      });
      if (r.data?.status === 'warn') {
        setWarn({ reason: r.data.reason, message: r.data.message });
        return;
      }
      setDraft(''); setWarn(null);
      await loadThread(threadId);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 422 && d?.code === 'message_blocked') {
        setBlock({ reason: d.reason, message: d.message });
      } else if (e?.response?.status === 403 && d?.code === 'messaging_restricted') {
        toast.error(d.message || 'Your messaging is restricted.');
      } else {
        toast.error(d?.message || d || 'Failed to send');
      }
    } finally {
      setSending(false);
    }
  };

  const initials = (other?.name || 'T').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" data-testid="discovery-message-page">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="discovery-msg-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Avatar className="w-9 h-9">
            {other?.photo ? <AvatarImage src={other.photo} alt={other?.name} /> : null}
            <AvatarFallback className="bg-cyan-100 text-cyan-700 text-xs">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-slate-900 truncate" data-testid="discovery-msg-name">{other?.name || 'Talent'}</h1>
            <p className="text-[11px] text-slate-500">Keep all communication on iEvntür</p>
          </div>
        </div>
        {origin && (
          <div className="max-w-3xl mx-auto px-4 pb-2">
            <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 inline-flex items-center gap-1">
              <Info className="w-3 h-3" /> {origin} · context only
            </span>
          </div>
        )}
      </header>

      <div className="flex-1 overflow-y-auto" data-testid="discovery-msg-messages">
        <div className="max-w-3xl mx-auto px-4 py-4 space-y-2">
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin text-cyan-600 inline" /></div>
          ) : messages.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500" data-testid="discovery-msg-empty">
              Start the conversation. Ask about availability, experience, wardrobe or the opportunity.
            </div>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={`flex ${m.is_own ? 'justify-end' : 'justify-start'}`} data-testid={`discovery-msg-${m.id}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.is_own ? 'bg-cyan-600 text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-900 rounded-bl-sm'}`}>
                  <div className="whitespace-pre-wrap break-words">{m.content}</div>
                  <div className={`text-[10px] mt-1 ${m.is_own ? 'text-cyan-100' : 'text-slate-500'}`}>
                    {m.created_at ? new Date(m.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      <footer className="bg-white border-t sticky bottom-0">
        <div className="max-w-3xl mx-auto px-3 py-3 space-y-2">
          {block && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-xs text-rose-800" data-testid="discovery-block-banner">
              <div className="flex items-center gap-1.5 font-semibold mb-1"><Ban className="w-4 h-4" /> Message blocked</div>
              <p>{block.message}</p>
              {block.reason && <p className="mt-1 italic opacity-80">Reason: {block.reason}</p>}
            </div>
          )}
          {warn && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="discovery-warn-banner">
              <div className="flex items-center gap-1.5 font-semibold mb-1"><ShieldAlert className="w-4 h-4" /> Please review</div>
              <p>{warn.message}</p>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={() => setWarn(null)} data-testid="discovery-warn-revise">Revise</Button>
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => doSend(true)} disabled={sending} data-testid="discovery-warn-send-anyway">Send anyway</Button>
              </div>
            </div>
          )}
          <div className="flex items-end gap-2">
            <Textarea
              value={draft}
              onChange={(e) => { setDraft(e.target.value); if (block) setBlock(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); doSend(false); } }}
              placeholder={`Message ${other?.name || 'talent'}…`}
              rows={1}
              className="min-h-[40px] resize-none"
              data-testid="discovery-msg-input"
            />
            <Button onClick={() => doSend(false)} disabled={sending || loading || !threadId || !draft.trim()} className="bg-cyan-600 hover:bg-cyan-700 text-white shrink-0" data-testid="discovery-msg-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DiscoveryMessage;
